import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

function useVisible(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const problems = [
  { icon: "ri-emotion-unhappy-line", label: "Swipe fatigue" },
  { icon: "ri-ghost-line", label: "Superficial matching" },
  { icon: "ri-chat-off-line", label: "Endless texting" },
  { icon: "ri-alarm-warning-line", label: "Unsafe meetups" },
];

const solutions = [
  { icon: "ri-sparkling-2-line", label: "AI matching" },
  { icon: "ri-group-line", label: "Communities" },
  { icon: "ri-calendar-event-line", label: "Real-life events" },
  { icon: "ri-star-line", label: "Reputation system" },
];

const teamValues = [
  {
    title: "User-first",
    desc: "Every feature we build starts with a simple question: does this help people connect more naturally?",
    icon: "ri-user-heart-line",
  },
  {
    title: "Transparency",
    desc: "We're honest about how our algorithm works and how your data is used. No dark patterns.",
    icon: "ri-eye-line",
  },
  {
    title: "Inclusivity",
    desc: "Aurays is built for everyone — regardless of background, orientation, or lifestyle.",
    icon: "ri-team-line",
  },
];

export default function AboutPage() {
  const hero = useVisible(0.1);
  const mission = useVisible(0.2);
  const problem = useVisible(0.2);
  const vision = useVisible(0.2);
  const team = useVisible(0.15);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-[radial-gradient(ellipse_at_top_right,_#E6D9FF_0%,_transparent_65%)] opacity-50" />
        </div>
        <div
          ref={hero.ref}
          className={`max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${hero.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex flex-col lg:flex-row items-center gap-14">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
                <i className="ri-information-line"></i>
                Our Story
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Built to fix
                <br />
                <span className="text-[#9B72FF]">modern dating.</span>
              </h1>
              <p className="text-gray-500 text-lg leading-relaxed max-w-lg mb-8">
                People don't want endless chatting. They want real connection. Aurays is our answer to a broken system.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Join Early Access
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex-1 relative">
              <div className="rounded-3xl overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(155,114,255,0.2)" }}>
                <img
                  src="aurays-real-connection-images.jpg"
                  alt="Real connections through Aurays"
                  className="w-full h-[320px] md:h-[380px] object-cover object-top"
                />
              </div>
              {/* floating card */}
              <div className="absolute bottom-6 left-6 bg-white rounded-2xl px-5 py-3 shadow-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-900">Founded in Hong Kong</p>
                <p className="text-xs text-gray-400 mt-0.5">2024 · O2O Dating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white py-20 md:py-24">
        <div
          ref={mission.ref}
          className={`max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${mission.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="max-w-2xl mx-auto text-center mb-16">
            <p className="text-xs font-semibold text-[#7C55CC] uppercase tracking-widest mb-3">Our Mission</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
              Connection that starts online,
              <br />
              <span className="text-[#9B72FF]">lives offline.</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed">
              Aurays combines AI matching, interest-based communities, curated real-life events, and a reputation system
              to create the world's most natural and trustworthy way to meet someone.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { num: "2,400+", label: "Early sign-ups" },
              { num: "O2O", label: "Dating model" },
              { num: "4", label: "Core features" },
              { num: "HK", label: "Launch market" },
            ].map((stat, i) => (
              <div key={i} className="bg-[#F5F0FF] rounded-3xl p-6 md:p-8 text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#7C55CC] mb-1">{stat.num}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="bg-[#0F0A1E] py-20 md:py-28">
        <div
          ref={problem.ref}
          className={`max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${problem.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The problem. Our solution.</h2>
            <p className="text-white/50 text-base max-w-md mx-auto">
              Modern dating is broken. We built Aurays to fix it — one feature at a time.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Problems */}
            <div className="bg-[#1A1030] rounded-3xl p-8 md:p-10">
              <p className="text-xs font-semibold text-red-400/70 uppercase tracking-widest mb-6">The Problem</p>
              <div className="flex flex-col gap-4">
                {problems.map((p, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 flex-shrink-0">
                      <i className={`${p.icon} text-red-400 text-lg`}></i>
                    </div>
                    <span className="text-sm text-white/60">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Solutions */}
            <div className="bg-gradient-to-br from-[#7C55CC] to-[#4A2D8A] rounded-3xl p-8 md:p-10">
              <p className="text-xs font-semibold text-[#C8B6FF]/70 uppercase tracking-widest mb-6">Aurays Combines</p>
              <div className="flex flex-col gap-4">
                {solutions.map((s, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 flex-shrink-0">
                      <i className={`${s.icon} text-white text-lg`}></i>
                    </div>
                    <span className="text-sm text-white font-medium">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]">
        <div
          ref={vision.ref}
          className={`max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${vision.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex flex-col lg:flex-row items-center gap-14">
            <div className="flex-1 relative">
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="/aurays-community-image.jpg"
                  alt="Aurays vision community"
                  className="w-full h-[300px] md:h-[380px] object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#7C55CC] uppercase tracking-widest mb-3">Our Vision</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                The default way people meet — <span className="text-[#9B72FF]">naturally, safely, meaningfully.</span>
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                We're not building another swipe app. We're building a new social infrastructure — one where meeting
                someone feels as natural as making a friend.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  "Remove the pressure of dating",
                  "Build trust through community",
                  "Bridge digital connection to real life",
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#E6D9FF] flex-shrink-0">
                      <i className="ri-check-line text-[#7C55CC] text-xs"></i>
                    </div>
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="bg-white py-20 md:py-24">
        <div
          ref={team.ref}
          className={`max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${team.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#7C55CC] uppercase tracking-widest mb-3">How We Work</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">What we stand for</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {teamValues.map((v, i) => (
              <div
                key={i}
                className="bg-[#F5F5F5] rounded-3xl p-8 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-300 cursor-default"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#E6D9FF]">
                  <i className={`${v.icon} text-xl text-[#7C55CC]`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{v.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-[#FAFAF9]">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#C8B6FF] to-[#E6D9FF] rounded-3xl px-8 py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1A0A3E] mb-3">Ready to meet for real?</h2>
          <p className="text-[#4A2D8A] text-base mb-8">Join early access — 2 months free premium waiting for you.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-gray-900 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Join Now
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
